import React, { useEffect, useRef, useState } from "react";
import type { User } from "firebase/auth";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import { shopAdminApi, ShopApiError } from "./shopAdminApi";
import type { CaseAssigneesPage, CaseRecipient } from "./caseTransferModel";

export default function CaseTransferRecipient({ user, value, blocked, onChange, onDenied }: {
  user: User; value: CaseRecipient | null; blocked: boolean; onChange: (value: CaseRecipient | null) => void; onDenied: () => void;
}) {
  const [page, setPage] = useState<CaseAssigneesPage | null>(null), [error, setError] = useState("");
  const [cursor, setCursor] = useState<string | undefined>(), [refresh, setRefresh] = useState(0), [loading, setLoading] = useState(false);
  const deny = useRef(onDenied); deny.current = onDenied;
  useEffect(() => {
    if (blocked) { setPage(null); setLoading(false); return; }
    let live = true; setPage(null); setError(""); setLoading(true);
    shopAdminApi.caseAssignees(user, cursor).then((next) => { if (live) { setPage(next); setLoading(false); } }).catch((failure) => {
      if (!live) return;
      setLoading(false); setPage(null);
      setError("Behöriga mottagare kunde inte hämtas. Kontrollera anslutning och aktuell roll.");
      if (failure instanceof ShopApiError && [401, 403].includes(failure.status)) deny.current();
    });
    return () => { live = false; };
  }, [user, cursor, refresh, blocked]);
  const restart = () => { onChange(null); setCursor(undefined); setRefresh((n) => n + 1); };
  const choices = value && !page?.staff.some((row) => row.uid === value.uid) ? [value, ...(page?.staff || [])] : page?.staff || [];
  return <section className="WebshopCaseReviewItem" aria-label="Överlämna ärendeansvar">
    <h4>Överlämna mitt ansvar</h4>
    <p>Endast andra aktiva ägarkonton visas med nuvarande behörigheter. Mottagarens aktuella behörighet kontrolleras igen när du bekräftar.</p>
    {error && !blocked && <Alert severity="error">{error}</Alert>}
    {loading && <p role="status">Hämtar behöriga mottagare…</p>}
    {page && <p role="status">Mottagare kontrollerade: {new Date(page.asOf).toLocaleString("sv-SE", { timeZone: "Europe/Stockholm" })}.</p>}
    {page && !page.staff.length && <p>Inga behöriga andra mottagare på denna sida. Rollen eller kontot kan behöva granskas; vyn beviljar ingen behörighet.</p>}
    {!!page?.omittedEntries && <Alert severity="warning">Vissa registerposter kunde inte användas. Granska roller/konton vid behov; välj inte en gissad mottagare.</Alert>}
    <TextField select fullWidth label="Mottagare av ärendet" value={value?.uid || ""} disabled={blocked || loading || !!error || !page}
      onChange={(event) => onChange(choices.find((row) => row.uid === event.target.value) || null)}>
      <MenuItem value="">Välj mottagare</MenuItem>{choices.map((row) => <MenuItem key={row.uid} value={row.uid} sx={{ whiteSpace: "normal", overflowWrap: "anywhere" }}>{row.label}</MenuItem>)}
    </TextField>
    {value && <p style={{ overflowWrap: "anywhere" }}>Vald mottagare: {value.label}</p>}
    {!blocked && <div className="WebshopKustomControls">
      <Button onClick={restart} disabled={loading}>Uppdatera mottagare</Button>
      {cursor && <Button onClick={restart} disabled={loading}>Första mottagarsidan</Button>}
      {page?.nextCursor && <Button onClick={() => setCursor(page.nextCursor!)} disabled={loading}>Nästa mottagarsida</Button>}
    </div>}
    <p>Högst 25 mottagare per sida. Överlämning behåller ärendets status och ändrar inte betalning, e-post eller lager.</p>
  </section>;
}
